// Host firewall (ADR 0009): `GET|PUT /firewall/central`,
// `GET|PUT /edges/:id/firewall`, `POST …/confirm`. Non-strict like the
// other response shapes; the policy shape mirrors @kalvio/contracts.
import { z } from 'zod/mini';

const nullableNumber = z.nullable(z.number());
const nullableString = z.nullable(z.string());

export const firewallExtraRule = z.object({
  proto: z.enum(['tcp', 'udp']),
  port: z.number(),
  source: z.string(),
  comment: z.optional(z.string()),
});
export type FirewallExtraRule = z.infer<typeof firewallExtraRule>;

export const firewallPolicy = z.object({
  ssh_allow: z.array(z.string()),
  lorawan_allow: z.array(z.string()),
  extra: z.array(firewallExtraRule),
});
export type FirewallPolicy = z.infer<typeof firewallPolicy>;

export const firewallFixedRule = z.object({
  proto: z.enum(['tcp', 'udp', 'any']),
  port: nullableNumber,
  source: z.string(),
  description: z.string(),
});
export type FirewallFixedRule = z.infer<typeof firewallFixedRule>;

export const firewallHost = z.object({
  role: z.enum(['central', 'edge']),
  ssh_port: z.number(),
  ssh_allow: z.array(z.string()),
  vpn_iface: nullableString,
  vpn_cidrs: z.array(z.string()),
  vpn_ssh: z.boolean(),
  public_tcp: z.array(z.number()),
  public_udp: z.array(z.number()),
  lorawan_allow: z.array(z.string()),
  lorawan_udp_port: z.number(),
  lorawan_tcp_port: z.number(),
  extra_allow: z.array(z.string()),
  bacnet_ifaces: z.array(z.string()),
  knx_ifaces: z.array(z.string()),
  ipv6: z.boolean(),
});
export type FirewallHost = z.infer<typeof firewallHost>;

export const firewallVersionStatus = z.enum([
  'requested',
  'pending',
  'applied',
  'rolled_back',
  'failed',
]);
export type FirewallVersionStatus = z.infer<typeof firewallVersionStatus>;

export const firewallHistoryItem = z.object({
  version: z.number(),
  status: firewallVersionStatus,
  error: nullableString,
  policy: firewallPolicy,
  requested_by: nullableString,
  created_at: z.number(),
  applied_at: nullableNumber,
  finished_at: nullableNumber,
});
export type FirewallHistoryItem = z.infer<typeof firewallHistoryItem>;

export const firewallView = z.object({
  target: z.enum(['central', 'edge']),
  /** central: the API has a spool directory (install.sh). */
  configured: z.boolean(),
  /** The host's kalvio-firewall watch reports. */
  managed: z.boolean(),
  reported_at: nullableNumber,
  /** Version of `policy` (base for the next PUT); null = none yet. */
  version: nullableNumber,
  policy: firewallPolicy,
  mandatory: z.array(firewallFixedRule),
  host: z.nullable(firewallHost),
  status: z.object({
    applied_version: nullableNumber,
    last_apply_at: nullableNumber,
    pending: z.nullable(z.object({ version: z.number(), deadline: z.number() })),
    requested: nullableNumber,
    last_error: z.nullable(
      z.object({ version: z.number(), status: z.string(), error: nullableString }),
    ),
    rolled_back: z.nullable(z.object({ version: z.number(), at: nullableNumber })),
    host_state: nullableString,
  }),
  /** Newest first. */
  history: z.array(firewallHistoryItem),
});
export type FirewallView = z.infer<typeof firewallView>;

/** Central itself or one edge. */
export type FirewallTargetId = { kind: 'central' } | { kind: 'edge'; edgeId: string };
